import { FC, useCallback, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import cloneDeep from 'lodash/cloneDeep'
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { ItemTemplate, MobileListItem, TemplateElement } from '@mobileTypes'
import { Image, TextFields, ViewModule } from '@mui/icons-material'
import { Box, Divider, Paper, Typography } from '@mui/material'

import { AvailableTemplateElementsPanel } from './AvailableTemplateElementsPanel'
import { ConfiguredTemplatePanel } from './ConfiguredTemplatePanel'
import { TemplateElementConfig, TemplateElementConfigDialog } from './TemplateElementConfigDialog'
import {
  addElement,
  getElementByPath,
  getParentItems,
  getParentPath,
  removeElement,
  updateTemplate,
} from './templateHelpers'

export const AdvancedConfigTab: FC = () => {
  const { watch, setValue } = useFormContext<MobileListItem>()

  const itemTemplate = watch('itemTemplate')
  const getDataCommands = watch('getDataCommands')

  // Prepare commands info for passing to dialog
  const commandsInfo = useMemo(() => {
    if (!getDataCommands?.length) return []

    return getDataCommands
      .map((cmd, index) => ({
        commandKey: `command${index + 1}`,
        commandName: cmd.name || `command${index + 1}`,
        objectCode: cmd.objectCode,
        objectTitle: cmd.objectTitle || cmd.objectCode,
      }))
      .filter(info => info.objectCode)
  }, [getDataCommands])

  // Note: Conversion from Simple to Advanced is now handled in MobileListItemCreateOrEdit
  // via ConfigModeSwitchDialog, not automatically here

  const currentTemplate: ItemTemplate = itemTemplate || {
    children: [],
  }

  const [configDialogState, setConfigDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    destination?: { path: string[]; index: number }
    elementPath?: string[]
    initialData?: Partial<TemplateElement>
  } | null>(null)

  // Track active dragging element
  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeElement, setActiveElement] = useState<TemplateElement | null>(null)
  const [dragType, setDragType] = useState<'sortable' | 'draggable' | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const [insertAfter, setInsertAfter] = useState<boolean>(false)

  // Track expanded state for each element by its path
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
    // По умолчанию раскрываем главный контейнер (путь "0")
    return new Set(['0'])
  })

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const toggleExpanded = useCallback((elementPath: string[]) => {
    const pathKey = elementPath.join(':')
    setExpandedItems(prev => {
      const next = new Set(prev)
      if (next.has(pathKey)) {
        next.delete(pathKey)
      } else {
        next.add(pathKey)
      }

      return next
    })
  }, [])

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event
      setActiveId(active.id as string)

      if (active.data.current?.type === 'draggable') {
        // New element from Available Elements
        setDragType('draggable')
        const elementType = (active.id as string).replace('NEW:', '') as 'view' | 'icon' | 'field'
        let newElement: TemplateElement
        if (elementType === 'view') {
          newElement = { type: 'view', children: [] }
        } else if (elementType === 'icon') {
          newElement = { type: 'icon', value: '', isJS: false }
        } else {
          newElement = {
            type: 'field',
            code: '',
            title: '',
            value: '',
            bindType: 'field',
            objectCode: '',
          }
        }
        setActiveElement(newElement)
      } else {
        // Existing element
        setDragType('sortable')
        const elementPath = (active.id as string).replace('ELEMENT:', '').split(':')
        const element = getElementByPath(elementPath, currentTemplate)
        setActiveElement(element)
      }
    },
    [currentTemplate]
  )

  const handleDragOver = useCallback((event: DragOverEvent) => {
    // Track which element is being dragged over for visual feedback
    if (event.over) {
      setOverId(event.over.id as string)

      // Determine if we should insert after or before the element based on cursor position
      if (event.over.rect && event.active.rect.current.translated) {
        const overRect = event.over.rect
        // Get the center Y position of the dragged element
        const activeCenterY =
          event.active.rect.current.translated.top + event.active.rect.current.translated.height / 2

        // Get the center Y position of the element being dragged over
        const overCenterY = overRect.top + overRect.height / 2

        // If dragged element center is below the over element center, insert after; otherwise insert before
        setInsertAfter(activeCenterY > overCenterY)
      } else if (event.delta) {
        // Fallback: use delta to determine direction
        setInsertAfter(event.delta.y > 0)
      } else {
        // Default to inserting before if we can't determine position
        setInsertAfter(false)
      }
    } else {
      setOverId(null)
      setInsertAfter(false)
    }
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      setActiveId(null)
      setActiveElement(null)
      setDragType(null)
      setOverId(null)
      setInsertAfter(false)

      if (!over) return

      // If dragging a new element, open config dialog
      if (dragType === 'draggable' && active.data.current?.type === 'draggable') {
        const elementType = (active.id as string).replace('NEW:', '') as 'view' | 'icon' | 'field'
        let parentId: string | undefined

        if (over.data.current?.type === 'droppable') {
          parentId = over.data.current.parent as string | undefined
        } else if ((over.id as string).startsWith('droppable-')) {
          const droppableId = (over.id as string).replace('droppable-', '')
          parentId = droppableId === 'ROOT' ? undefined : droppableId
        } else if (over.data.current?.type === 'item') {
          // Dropped over an existing element - use its parent
          parentId = over.data.current.parent as string | undefined
        }

        // ЗАПРЕТ: Нельзя добавлять новые элементы на уровень ROOT
        // На ROOT может быть только один главный View контейнер
        if (parentId === undefined) {
          const hasMainContainer = currentTemplate.children && currentTemplate.children.length > 0
          if (hasMainContainer) {
            // Если уже есть главный контейнер, нельзя добавлять на ROOT
            return
          }
          // Если ROOT пустой, можно добавить только View контейнер
          if (elementType !== 'view') {
            return
          }
        }

        const parentPath = parentId ? parentId.split(':') : undefined

        // Get current items count to determine index
        const parentItems = getParentItems(parentPath, currentTemplate)
        const index = parentItems.length

        setConfigDialogState({
          open: true,
          mode: 'create',
          destination: { path: parentPath || [], index },
          initialData: { type: elementType } as Partial<TemplateElement>,
        })

        return
      }

      // Reordering existing elements
      if (dragType === 'sortable' && over.data.current?.type === 'item') {
        const activeIdValue = (active.id as string).replace('ELEMENT:', '')
        const activeElementPath = activeIdValue.split(':')
        const activeItem = getElementByPath(activeElementPath, currentTemplate)

        if (!activeItem) return

        const overId = over.data.current.id as string
        const overElementPath = overId.split(':')
        const overParentId = over.data.current.parent as string | undefined
        const overParentPath = overParentId ? overParentId.split(':') : undefined

        // Get current parent
        const currentParentPath = getParentPath(activeElementPath)
        const currentParentId =
          currentParentPath.length > 0 ? currentParentPath.join(':') : undefined

        // Check if dropping on itself
        if (activeElementPath.join(':') === overElementPath.join(':')) {
          return
        }

        // Prevent dropping main container (first root element)
        if (activeElementPath.length === 1 && activeElementPath[0] === '0') {
          const firstElement = currentTemplate.children?.[0]
          if (firstElement && firstElement.type === 'view') {
            return
          }
        }

        // ЗАПРЕТ: Нельзя перетаскивать элементы на уровень ROOT (кроме главного контейнера)
        if (
          overParentId === undefined &&
          !(activeElementPath.length === 1 && activeElementPath[0] === '0')
        ) {
          return
        }

        let newTemplate = cloneDeep(currentTemplate)

        // Same parent - reorder
        if (overParentId === currentParentId) {
          const parentItems = getParentItems(overParentPath, newTemplate)
          const activeIndex = parentItems.findIndex((_, idx) => {
            const path = overParentPath ? [...overParentPath, idx.toString()] : [idx.toString()]

            return path.join(':') === activeElementPath.join(':')
          })
          const overIndex = parentItems.findIndex((_, idx) => {
            const path = overParentPath ? [...overParentPath, idx.toString()] : [idx.toString()]

            return path.join(':') === overElementPath.join(':')
          })

          if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
            const reordered = arrayMove(parentItems, activeIndex, overIndex)
            newTemplate = updateTemplate(newTemplate, overParentPath, reordered)
            setValue('itemTemplate', newTemplate, { shouldDirty: true })
          }
        } else {
          // Different parent - move to new location
          // Remove from original
          newTemplate = removeElement(newTemplate, activeElementPath)

          // Add to new location at the position of the over element
          const overParentItems = getParentItems(overParentPath, newTemplate)
          const overIndex = overParentItems.findIndex((_, idx) => {
            const path = overParentPath ? [...overParentPath, idx.toString()] : [idx.toString()]

            return path.join(':') === overElementPath.join(':')
          })

          if (overIndex !== -1) {
            const newItems = [...overParentItems]
            newItems.splice(overIndex, 0, activeItem)
            newTemplate = updateTemplate(newTemplate, overParentPath, newItems)
          } else {
            newTemplate = addElement(newTemplate, overParentPath, activeItem)
          }

          setValue('itemTemplate', newTemplate, { shouldDirty: true })
        }
      } else if (
        dragType === 'sortable' &&
        (over.data.current?.type === 'droppable' || (over.id as string).startsWith('droppable-'))
      ) {
        // Dropping into empty droppable area
        const activeIdValue = (active.id as string).replace('ELEMENT:', '')
        const activeElementPath = activeIdValue.split(':')
        const activeItem = getElementByPath(activeElementPath, currentTemplate)

        if (!activeItem) return

        let overParentId: string | undefined
        if (over.data.current?.type === 'droppable') {
          overParentId = over.data.current.parent as string | undefined
        } else {
          const droppableId = (over.id as string).replace('droppable-', '')
          overParentId = droppableId === 'ROOT' ? undefined : droppableId
        }
        const overParentPath = overParentId ? overParentId.split(':') : undefined

        // Check if already in target location
        const currentParentPath = getParentPath(activeElementPath)
        const currentParentId =
          currentParentPath.length > 0 ? currentParentPath.join(':') : undefined

        if (overParentId === currentParentId) {
          // Already in the same parent, no need to move
          return
        }

        // Prevent dropping main container
        if (activeElementPath.length === 1 && activeElementPath[0] === '0') {
          const firstElement = currentTemplate.children?.[0]
          if (firstElement && firstElement.type === 'view') {
            return
          }
        }

        // ЗАПРЕТ: Нельзя перетаскивать элементы в ROOT (кроме главного контейнера)
        if (
          overParentId === undefined &&
          !(activeElementPath.length === 1 && activeElementPath[0] === '0')
        ) {
          return
        }

        let newTemplate = cloneDeep(currentTemplate)

        // Remove from original location
        newTemplate = removeElement(newTemplate, activeElementPath)

        // Add to new location
        newTemplate = addElement(newTemplate, overParentPath, activeItem)

        setValue('itemTemplate', newTemplate, { shouldDirty: true })
      }
    },
    [dragType, currentTemplate, setValue]
  )

  const handleSaveElementConfig = useCallback(
    (config: TemplateElementConfig) => {
      if (!configDialogState) return

      const updatedTemplate = { ...currentTemplate }

      if (configDialogState.mode === 'create') {
        const elements = getParentItems(
          configDialogState.destination!.path,
          updatedTemplate
        ).filter(Boolean)
        const newElement = config as TemplateElement
        elements.splice(configDialogState.destination!.index, 0, newElement)
        const parentPath =
          configDialogState.destination!.path.length > 0
            ? configDialogState.destination!.path
            : undefined
        const finalTemplate = updateTemplate(updatedTemplate, parentPath, elements.filter(Boolean))
        setValue('itemTemplate', finalTemplate, { shouldDirty: true })
      } else {
        // Edit mode - update element at path
        const elementPath = configDialogState.elementPath!
        const pathToParent = elementPath.slice(0, -1)
        const elementIndex = parseInt(elementPath[elementPath.length - 1])
        const elements = getParentItems(
          pathToParent.length > 0 ? pathToParent : undefined,
          updatedTemplate
        ).filter(Boolean)
        elements[elementIndex] = config as TemplateElement
        const parentPath = pathToParent.length > 0 ? pathToParent : undefined
        const finalTemplate = updateTemplate(updatedTemplate, parentPath, elements.filter(Boolean))
        setValue('itemTemplate', finalTemplate, { shouldDirty: true })
      }

      setConfigDialogState(null)
    },
    [configDialogState, currentTemplate, setValue]
  )

  const handleEditElement = useCallback(
    (elementPath: string[]) => {
      const element = getElementByPath(elementPath, currentTemplate)
      if (!element) return

      setConfigDialogState({
        open: true,
        mode: 'edit',
        elementPath,
        initialData: element,
      })
    },
    [currentTemplate]
  )

  const handleDeleteElement = useCallback(
    (elementPath: string[]) => {
      // Prevent deleting main container (first element in ROOT if it's a View)
      if (elementPath.length === 1 && elementPath[0] === '0') {
        const firstElement = currentTemplate.children?.[0]
        if (firstElement && firstElement.type === 'view') {
          return
        }
      }

      const updatedTemplate = removeElement(currentTemplate, elementPath)
      setValue('itemTemplate', updatedTemplate, { shouldDirty: true })
    },
    [currentTemplate, setValue]
  )

  const getDragOverlay = () => {
    if (!activeId || !activeElement) return null

    return (
      <Paper
        variant='outlined'
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: 'background.paper',
          borderColor: 'primary.main',
          boxShadow: 6,
          minWidth: 200,
          opacity: 0.9,
        }}
      >
        {activeElement.type === 'view' && <ViewModule fontSize='small' />}
        {activeElement.type === 'icon' && <Image fontSize='small' />}
        {activeElement.type === 'field' && <TextFields fontSize='small' />}
        <Typography variant='body2'>
          {activeElement.type === 'view'
            ? 'View Container'
            : activeElement.type === 'icon'
            ? activeElement.value || 'Icon'
            : activeElement.code || activeElement.title || 'Field'}
        </Typography>
      </Paper>
    )
  }

  return (
    <>
      <style>
        {`
          /* Глобально отключаем все transitions во время драга */
          ${activeId ? '* { transition: none !important; }' : ''}
        `}
      </style>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        autoScroll={{ enabled: false }}
      >
        <Box sx={{ display: 'flex', height: '600px', gap: 2 }}>
          <AvailableTemplateElementsPanel />
          <Divider orientation='vertical' flexItem />
          <ConfiguredTemplatePanel
            template={currentTemplate}
            onEditElement={handleEditElement}
            onDeleteElement={handleDeleteElement}
            activeId={activeId}
            overId={overId}
            insertAfter={insertAfter}
            activeElement={activeElement}
            expandedItems={expandedItems}
            onToggleExpanded={toggleExpanded}
          />
        </Box>
        <DragOverlay dropAnimation={null}>{getDragOverlay()}</DragOverlay>
      </DndContext>

      {configDialogState && (
        <TemplateElementConfigDialog
          open={configDialogState.open}
          mode={configDialogState.mode}
          initialData={configDialogState.initialData}
          commands={commandsInfo}
          onClose={() => setConfigDialogState(null)}
          onSave={handleSaveElementConfig}
        />
      )}
    </>
  )
}
